import { loadProgress, saveProgress } from '../shared/progress';
import { getMistakes, markRetried, tagErrorType, deriveQuestionId } from '../shared/mistake-log';
import type { MistakeEntry, Subject } from '../shared/types';
import { findOrCreateKP, recordAttempt } from '../shared/student-kp';
import { speak, stopSpeaking } from '../shared/tts-client';
import { textComplete } from './text-complete-client';
import { generateSimilarQuestion, normalizeAnswer } from './mistake-similar';
import { mountEscapeButton } from './escape-button';
import { runReviewPaper } from './review/review-paper';
import { ensureAcademicTheme } from './ui/theme';

/**
 * 錯題本 — 學術風格設計系統(boss 2026-06-08;見 wiki page-470f59c0)。
 *
 * 整頁套紙底頁殼(`.ef-page` + serif 標題列);本畫面自管 escape 生命週期,故手動
 * 組頁殼(不走 pageShell 的 onBack,避免多掛一顆 ✕,作法同 exam-bank/practice)。
 * 錯題清單 / 詳情卡用紙片樣式(`.ef-card` token);生成複習考卷 / 重做 = 主按鈕、
 * 返回 = 次按鈕、錯因標籤 = 紙色 pill。
 *
 * 行為 / class / id 全保留(`.mistake-book`、`.mistake-item`、`.mistake-count`、
 * `.mistake-empty`、`.mistake-list`、`.mistake-detail`、`.detail-prompt`、
 * `.detail-answers`、`.back-btn`、`.retry-btn`、`.retry-input`、`#retry-submit`、
 * `.retry-feedback`、`.error-type-btn[data-type]` 等),測試依賴的節點不動。
 */

const SUBJECT_LABEL: Record<string, string> = {
  math: '數學',
  chinese: '國語',
  english: '英語',
  nature: '自然',
  social: '社會',
};

const ERROR_TYPE_LABEL: Record<NonNullable<MistakeEntry['errorType']>, string> = {
  careless: '粗心',
  concept: '觀念不清',
  method: '方法錯誤',
  time: '時間不夠',
};

function relativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return '剛才';
  if (minutes < 60) return `${minutes} 分鐘前`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} 小時前`;
  const days = Math.floor(hours / 24);
  return `${days} 天前`;
}

function escapeHtml(s: string): string {
  const d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
}

/**
 * 「為什麼錯 / 怎麼算」要顯示的說明。優先用 entry.explanation(OCR 自動批改 /
 * 產生器帶入);舊存檔沒有就給一句中性 fallback,讓詳情永遠有東西教。
 */
function explanationText(entry: MistakeEntry): string {
  const stored = entry.explanation?.trim();
  if (stored) return stored;
  return `正確答案是 ${String(entry.correctAnswer)}。對照一下你的算法,看看是哪一步不一樣。`;
}

/**
 * 英文題用雲端自然嗓(發音準),其餘(中文為主)用裝置內建嗓 —— 與 dictation.ts
 * 慣例一致。
 */
function voiceForSubject(subject: string): 'cloud' | 'device' {
  return subject === 'english' ? 'cloud' : 'device';
}

/** 唸給孩子聽的整段文字:題目 + 說明。 */
function speakText(entry: MistakeEntry): string {
  return `${entry.prompt}。${explanationText(entry)}`;
}

/**
 * Renders the Mistake Book UI into `container`.
 * onBack: called when the user exits.
 */
export function renderMistakeBook(container: HTMLElement, onBack: () => void): void {
  const progress = loadProgress();
  if (!progress) { onBack(); return; }

  ensureAcademicTheme();
  ensureMistakeBookStyle();

  // 紙底頁殼(手動組:本畫面自管 escape,不走 pageShell 的 onBack)。
  const page = document.createElement('div');
  page.className = 'ef-page';
  const inner = document.createElement('div');
  inner.className = 'ef-page__inner';
  page.appendChild(inner);

  const header = document.createElement('header');
  header.className = 'ef-page__header';
  const heading = document.createElement('div');
  heading.className = 'ef-page__heading';
  const h1 = document.createElement('h1');
  h1.className = 'ef-title';
  h1.textContent = '錯題本';
  const sub = document.createElement('p');
  sub.className = 'ef-subtitle';
  sub.textContent = '把寫錯的題目再練一次,做對了就從待複習清單移除。';
  heading.append(h1, sub);
  header.appendChild(heading);
  inner.appendChild(header);

  const root = document.createElement('div');
  root.className = 'mistake-book ef-page__body';
  inner.appendChild(root);

  container.appendChild(page);

  const unmountEscape = mountEscapeButton(() => {
    unmountEscape();
    page.remove();
    onBack();
  });

  function renderList(): void {
    root.innerHTML = '';

    const mistakes = getMistakes(progress!, { excludeRetried: false });
    const pending = mistakes.filter((m) => !m.retried);

    const headerEl = document.createElement('div');
    headerEl.className = 'mistake-book-header';
    headerEl.innerHTML = `
      <p class="mistake-count">${pending.length > 0 ? `${pending.length} 題待複習` : '目前沒有待複習的題目。'}</p>
      <button class="review-paper-btn ef-btn ef-btn--primary">生成複習考卷</button>
    `;
    root.appendChild(headerEl);

    headerEl.querySelector('.review-paper-btn')!.addEventListener('click', () => {
      unmountEscape();
      root.innerHTML = '';
      void runReviewPaper(root, () => {
        page.remove();
        onBack();
      });
    });

    if (mistakes.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'mistake-empty';
      empty.textContent = '你還沒有錯題,繼續加油!';
      root.appendChild(empty);
      return;
    }

    const list = document.createElement('ul');
    list.className = 'mistake-list';

    for (const entry of mistakes) {
      const li = document.createElement('li');
      li.className = `mistake-item${entry.retried ? ' retried' : ''}`;
      li.innerHTML = `
        <div class="mistake-row">
          <span class="mistake-subject">${escapeHtml(SUBJECT_LABEL[entry.subject] ?? entry.subject)}</span>
          <span class="mistake-kp">${escapeHtml(entry.kp)}</span>
          <span class="mistake-time">${escapeHtml(relativeTime(entry.timestamp))}</span>
          ${entry.retried ? '<span class="retried-badge">已複習</span>' : ''}
          ${entry.errorType ? `<span class="error-type-badge">${escapeHtml(ERROR_TYPE_LABEL[entry.errorType] ?? entry.errorType)}</span>` : ''}
        </div>
      `;
      li.addEventListener('click', () => renderDetail(entry));
      list.appendChild(li);
    }

    root.appendChild(list);
  }

  function renderDetail(entry: MistakeEntry): void {
    root.innerHTML = '';

    const detail = document.createElement('div');
    detail.className = 'mistake-detail';

    const userAnswerDisplay = entry.userAnswer === null ? '(沒有作答)' : String(entry.userAnswer);
    const errorTypeBtns = (['careless', 'concept', 'method', 'time'] as const)
      .map((t) => `<button class="error-type-btn${entry.errorType === t ? ' selected' : ''}" data-type="${t}">${ERROR_TYPE_LABEL[t]}</button>`)
      .join('');

    detail.innerHTML = `
      <button class="back-btn ef-btn ef-btn--secondary">← 返回</button>
      <div class="detail-subject">${escapeHtml(SUBJECT_LABEL[entry.subject] ?? entry.subject)} · ${escapeHtml(entry.kp)}</div>
      <div class="detail-prompt card">${escapeHtml(entry.prompt)}</div>
      <div class="detail-answers">
        <div class="answer-row wrong-answer">你的答案:${escapeHtml(userAnswerDisplay)}</div>
        <div class="answer-row correct-answer">正確答案:${escapeHtml(String(entry.correctAnswer))}</div>
      </div>
      <div class="explain-section">
        <div class="explain-head">
          <p class="explain-label">為什麼錯 · 怎麼算</p>
          <button class="speak-btn ef-btn ef-btn--secondary" type="button" aria-label="唸給我聽">🔊 唸給我聽</button>
        </div>
        <p class="explain-text">${escapeHtml(explanationText(entry))}</p>
        <button class="explain-more-btn ef-btn ef-btn--secondary" type="button">想更詳細的步驟 →</button>
        <div class="explain-more" hidden></div>
      </div>
      <div class="error-type-section">
        <p class="error-type-label">這題對你來說是?</p>
        <div class="error-type-btns">${errorTypeBtns}</div>
      </div>
      <div class="retry-section">
        ${entry.retried
          ? '<p class="retried-msg">你已經做對這題了!</p>'
          : '<button class="retry-btn primary-btn ef-btn ef-btn--primary">重新做一遍</button>'}
      </div>
      <div class="similar-section">
        <button class="similar-btn ef-btn ef-btn--primary" type="button">再做一題類似的 →</button>
      </div>
    `;

    root.appendChild(detail);

    // Back button
    detail.querySelector('.back-btn')!.addEventListener('click', () => {
      stopSpeaking();
      renderList();
    });

    // 🔊 唸給我聽:題目 + 說明,英文題雲端嗓、其餘裝置嗓(同 dictation 慣例)。
    const speakBtn = detail.querySelector('.speak-btn') as HTMLButtonElement | null;
    speakBtn?.addEventListener('click', () => {
      void speak(speakText(entry), { lang: entry.subject === 'english' ? 'en' : 'zh-TW', voice: voiceForSubject(entry.subject) });
    });

    // 「更詳細」:呼 LLM 生步驟;失敗 / 無 bridge 不阻斷,顯示降級訊息。
    const moreBtn = detail.querySelector('.explain-more-btn') as HTMLButtonElement | null;
    const moreBox = detail.querySelector('.explain-more') as HTMLElement | null;
    moreBtn?.addEventListener('click', () => {
      if (!moreBtn || !moreBox) return;
      moreBtn.disabled = true;
      moreBtn.textContent = '思考中…';
      void (async () => {
        const steps = await textComplete({
          system: '你是台灣國小三年級的老師,只用繁體中文,用親切口吻、分點寫清楚的解題步驟,不要太長。',
          prompt: `題目:${entry.prompt}\n正確答案:${String(entry.correctAnswer)}\n請一步一步教這題怎麼算,讓三年級小孩看得懂。`,
        });
        moreBox.hidden = false;
        if (steps) {
          moreBox.textContent = steps;
          moreBtn.remove();
        } else {
          moreBox.textContent = '現在沒辦法生更詳細的步驟,先看上面的說明,或按「再做一題類似的」練習看看。';
          moreBtn.disabled = false;
          moreBtn.textContent = '想更詳細的步驟 →';
        }
      })();
    });

    // Error type tagging
    detail.querySelectorAll('.error-type-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const t = (btn as HTMLElement).dataset['type'] as MistakeEntry['errorType'];
        tagErrorType(progress!, entry.questionId, t);
        entry.errorType = t;
        saveProgress(progress!);
        // Re-render detail to reflect selection
        renderDetail(entry);
      });
    });

    // Retry button: show the question inline
    const retryBtn = detail.querySelector('.retry-btn');
    retryBtn?.addEventListener('click', () => renderRetry(entry, detail));

    // 再做一題類似:同 KP 生新題 → 即時對錯 → 答對升 box。
    const similarBtn = detail.querySelector('.similar-btn') as HTMLButtonElement | null;
    similarBtn?.addEventListener('click', () => renderSimilar(entry, detail));
  }

  /**
   * 「再做一題類似的」:同 KP 生一題(數學用產生器,其餘用 LLM),小孩作答後
   * 即時回饋。答對 → `recordAttempt(kp,'correct')` 升 Leitner box(純學習側入帳,
   * 只動 factionXP/box,不碰戰鬥)。答錯給再試,不強制降 box。
   */
  function renderSimilar(entry: MistakeEntry, detailEl: HTMLElement): void {
    const section = detailEl.querySelector('.similar-section') as HTMLElement;
    section.innerHTML = '<p class="similar-loading">出題中…</p>';

    void (async () => {
      const q = await generateSimilarQuestion(entry.kp, entry.subject, entry.prompt);
      if (!q) {
        section.innerHTML =
          '<p class="similar-loading">現在沒辦法出類似題,先用上面的「重新做一遍」練這題就好。</p>';
        return;
      }

      section.innerHTML = `
        <div class="similar-card">
          <p class="similar-tag">再做一題類似的</p>
          <div class="similar-prompt card">${escapeHtml(q.prompt)}</div>
          <div class="fill-row">
            <input type="text" class="fill-input similar-input" placeholder="輸入答案" />
            <button class="primary-btn ef-btn ef-btn--primary similar-submit" type="button">送出</button>
          </div>
          <div class="similar-feedback"></div>
        </div>
      `;

      const input = section.querySelector('.similar-input') as HTMLInputElement;
      const submitBtn = section.querySelector('.similar-submit') as HTMLButtonElement;
      const feedback = section.querySelector('.similar-feedback') as HTMLElement;
      let answered = false;

      const submit = (): void => {
        if (answered) return;
        const got = normalizeAnswer(input.value);
        if (got === '') { input.focus(); return; }
        const correct = q.acceptedAnswers.includes(got);
        if (correct) {
          answered = true;
          // ✅ 純學習側入帳:升 Leitner box(不碰戰鬥 / commanderXp)。
          const kp = findOrCreateKP(progress!, entry.kp, entry.subject as Subject);
          recordAttempt(kp, 'correct', new Date().toISOString());
          saveProgress(progress!);
          feedback.className = 'similar-feedback correct';
          feedback.innerHTML = `答對了!正解是 <strong>${escapeHtml(q.displayAnswer)}</strong>${q.explanation ? ` —— ${escapeHtml(q.explanation)}` : ''}`;
          input.disabled = true;
          submitBtn.remove();
        } else {
          // 答錯不降 box,給再試。
          feedback.className = 'similar-feedback wrong';
          feedback.textContent = '再試試,先想想剛剛上面教的算法。';
          input.value = '';
          input.focus();
        }
      };

      submitBtn.addEventListener('click', submit);
      input.addEventListener('keydown', (e) => { if (e.key === 'Enter') submit(); });
      input.focus();
    })();
  }

  function renderRetry(entry: MistakeEntry, detailEl: HTMLElement): void {
    // Replace the retry section with an inline fill question
    const retrySection = detailEl.querySelector('.retry-section')!;
    retrySection.innerHTML = `
      <div class="retry-prompt card">${escapeHtml(entry.prompt)}</div>
      <div class="fill-row">
        <input type="text" class="fill-input retry-input" placeholder="輸入答案" autofocus />
        <button class="primary-btn ef-btn ef-btn--primary" id="retry-submit">送出</button>
      </div>
      <div class="retry-feedback"></div>
    `;

    const input = retrySection.querySelector('.retry-input') as HTMLInputElement;
    const feedback = retrySection.querySelector('.retry-feedback') as HTMLElement;

    const submit = (): void => {
      const raw = input.value.trim();
      const correct = String(entry.correctAnswer).trim();
      if (raw === correct) {
        // Correct! Mark retried.
        markRetried(progress!, entry.questionId);
        entry.retried = true;
        saveProgress(progress!);
        feedback.className = 'retry-feedback correct';
        feedback.innerHTML = `答對了!正解是 <strong>${escapeHtml(correct)}</strong>`;
        retrySection.querySelector('#retry-submit')?.remove();
        input.disabled = true;
        // Update the list badge after short delay
        setTimeout(() => renderDetail(entry), 1200);
      } else {
        // Wrong again — update timestamp & userAnswer in storage
        const existingIdx = progress!.mistakes.findIndex(
          (m) => m.questionId === entry.questionId,
        );
        if (existingIdx !== -1) {
          progress!.mistakes[existingIdx].timestamp = Date.now();
          progress!.mistakes[existingIdx].userAnswer = raw;
          entry.userAnswer = raw;
          entry.timestamp = Date.now();
        }
        saveProgress(progress!);
        feedback.className = 'retry-feedback wrong';
        feedback.innerHTML = `再試試!正解是 <strong>${escapeHtml(correct)}</strong>`;
        input.value = '';
        input.focus();
      }
    };

    retrySection.querySelector('#retry-submit')!.addEventListener('click', submit);
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') submit(); });
    input.focus();
  }

  renderList();
}

/** 錯題本 scoped 樣式(只用 ef- token,不碰全域 styles.css)。冪等注入。 */
const MISTAKE_BOOK_STYLE_ID = 'ef-mistake-book-style';
function ensureMistakeBookStyle(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(MISTAKE_BOOK_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = MISTAKE_BOOK_STYLE_ID;
  style.textContent = `
.mistake-book { display:flex; flex-direction:column; gap:16px; color:var(--ef-ink-soft); font-family:var(--ef-sans); }
.mistake-book-header { display:flex; flex-direction:column; gap:10px; align-items:flex-start; }
.mistake-book .mistake-count { margin:0; font-size:15px; color:var(--ef-sub); }
.mistake-book .mistake-empty { margin:8px 0 0; font-size:15px; color:var(--ef-sub); }

.mistake-book .mistake-list { list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:10px; }
.mistake-book .mistake-item {
  padding:14px 16px; background:var(--ef-paper-bright); border:1px solid var(--ef-line);
  border-radius:var(--ef-radius); box-shadow:var(--ef-shadow-card); cursor:pointer;
  transition:transform 0.12s ease, box-shadow 0.18s ease, border-color 0.18s ease;
  -webkit-tap-highlight-color:transparent; touch-action:manipulation;
}
.mistake-book .mistake-item:hover { transform:translateY(-2px); box-shadow:var(--ef-shadow-lift); border-color:#d2c9ad; }
.mistake-book .mistake-item.retried { opacity:0.62; }
.mistake-book .mistake-row { display:flex; flex-wrap:wrap; align-items:center; gap:10px; }
.mistake-book .mistake-subject { font-family:var(--ef-serif); font-weight:700; font-size:15px; color:var(--ef-ink); }
.mistake-book .mistake-kp { font-size:13px; color:var(--ef-sub); }
.mistake-book .mistake-time { margin-left:auto; font-size:12px; color:var(--ef-sub); }
.mistake-book .retried-badge,
.mistake-book .error-type-badge {
  font-size:12px; padding:3px 10px; border-radius:var(--ef-radius-pill); letter-spacing:0.5px;
}
.mistake-book .retried-badge { background:rgba(31,111,92,0.12); color:var(--ef-green-dark); }
.mistake-book .error-type-badge { background:var(--ef-note); color:var(--ef-ink-soft); }

.mistake-book .mistake-detail { display:flex; flex-direction:column; gap:14px; }
.mistake-book .detail-subject { font-family:var(--ef-serif); font-weight:600; font-size:16px; color:var(--ef-ink); }
.mistake-book .detail-prompt {
  padding:16px; background:var(--ef-paper-bright); border:1px solid var(--ef-line);
  border-radius:var(--ef-radius); box-shadow:var(--ef-shadow-card);
  font-family:var(--ef-serif); font-size:18px; line-height:1.5; color:var(--ef-ink);
}
.mistake-book .detail-answers { display:flex; flex-direction:column; gap:6px; }
.mistake-book .answer-row { font-size:14px; padding:8px 12px; border-radius:var(--ef-radius-sm); }
.mistake-book .answer-row.wrong-answer { background:rgba(179,38,30,0.07); color:#8a201a; }
.mistake-book .answer-row.correct-answer { background:rgba(31,111,92,0.09); color:var(--ef-green-dark); }

/* 解析 —「為什麼錯 · 怎麼算」+ 唸給我聽 + 更詳細(講義便箋紙感) */
.mistake-book .explain-section {
  display:flex; flex-direction:column; gap:10px;
  padding:14px 16px; background:var(--ef-note); border:1px solid var(--ef-line);
  border-radius:var(--ef-radius); box-shadow:var(--ef-shadow-card);
}
.mistake-book .explain-head { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.mistake-book .explain-label {
  margin:0; font-family:var(--ef-serif); font-weight:700; font-size:15px; color:var(--ef-ink);
}
.mistake-book .speak-btn { margin-left:auto; align-self:center; min-height:36px; padding:6px 14px; font-size:13px; }
.mistake-book .explain-text { margin:0; font-size:15px; line-height:1.65; color:var(--ef-ink-soft); }
.mistake-book .explain-more-btn { align-self:flex-start; min-height:36px; padding:6px 14px; font-size:13px; }
.mistake-book .explain-more {
  margin:0; font-size:14px; line-height:1.7; color:var(--ef-ink-soft);
  white-space:pre-wrap; padding-top:4px; border-top:1px dashed var(--ef-line);
}

.mistake-book .error-type-section { display:flex; flex-direction:column; gap:8px; }
.mistake-book .error-type-label { margin:0; font-size:14px; font-weight:600; color:var(--ef-ink-soft); }
.mistake-book .error-type-btns { display:flex; flex-wrap:wrap; gap:8px; }
.mistake-book .error-type-btn {
  min-height:36px; padding:7px 14px; border:1px solid var(--ef-line); border-radius:var(--ef-radius-pill);
  background:var(--ef-paper-bright); color:var(--ef-ink-soft); font-family:var(--ef-sans); font-size:13px;
  font-weight:600; cursor:pointer; transition:background 0.16s ease, color 0.16s ease, border-color 0.16s ease;
  -webkit-tap-highlight-color:transparent; touch-action:manipulation;
}
.mistake-book .error-type-btn:hover { border-color:var(--ef-green); background:rgba(31,111,92,0.06); }
.mistake-book .error-type-btn.selected { background:var(--ef-green); color:#fff; border-color:var(--ef-green); }

.mistake-book .retry-section { display:flex; flex-direction:column; gap:10px; }
.mistake-book .retried-msg { margin:0; font-size:14px; color:var(--ef-green-dark); font-weight:600; }
.mistake-book .retry-prompt {
  padding:14px 16px; background:var(--ef-paper-bright); border:1px solid var(--ef-line);
  border-radius:var(--ef-radius); font-family:var(--ef-serif); font-size:16px; line-height:1.5; color:var(--ef-ink);
}
.mistake-book .fill-row { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
.mistake-book .fill-input {
  flex:1 1 160px; min-height:44px; padding:10px 14px; border:1px solid var(--ef-line);
  border-radius:var(--ef-radius-sm); background:#fff; font-family:var(--ef-sans); font-size:16px; color:var(--ef-ink);
}
.mistake-book .fill-input:focus-visible { outline:3px solid rgba(31,111,92,0.35); outline-offset:1px; border-color:var(--ef-green); }
.mistake-book .retry-feedback { font-size:14px; }
.mistake-book .retry-feedback.correct { color:var(--ef-green-dark); }
.mistake-book .retry-feedback.wrong { color:#b3261e; }

/* 再做一題類似 */
.mistake-book .similar-section { display:flex; flex-direction:column; gap:10px; }
.mistake-book .similar-loading { margin:0; font-size:14px; color:var(--ef-sub); }
.mistake-book .similar-card {
  display:flex; flex-direction:column; gap:10px;
  padding:14px 16px; background:var(--ef-paper-bright); border:1px solid var(--ef-line);
  border-radius:var(--ef-radius); box-shadow:var(--ef-shadow-card);
}
.mistake-book .similar-tag { margin:0; font-size:13px; font-weight:600; color:var(--ef-green-dark); letter-spacing:0.5px; }
.mistake-book .similar-prompt {
  padding:12px 14px; background:#fff; border:1px solid var(--ef-line);
  border-radius:var(--ef-radius-sm); font-family:var(--ef-serif); font-size:17px; line-height:1.5; color:var(--ef-ink);
}
.mistake-book .similar-feedback { font-size:14px; }
.mistake-book .similar-feedback.correct { color:var(--ef-green-dark); }
.mistake-book .similar-feedback.wrong { color:#b3261e; }

.mistake-book .review-paper-btn,
.mistake-book .retry-btn,
.mistake-book #retry-submit,
.mistake-book .similar-btn,
.mistake-book .back-btn { align-self:flex-start; }
@media (prefers-reduced-motion: reduce) {
  .mistake-book .mistake-item, .mistake-book .mistake-item:hover,
  .mistake-book .error-type-btn { transition:none; transform:none; }
}
`;
  document.head.appendChild(style);
}

// Re-export deriveQuestionId for convenience (used in app.ts integration)
export { deriveQuestionId };
